import { migrationInFlightAtom } from '@/atoms/chatMigrationAtoms';
import { storeChatsFromLocalStorage } from '@/pages/api/scribeAPIV2';
import type {
  ChatMessageResponse,
  ChatResponse,
  LocalStorageChatMessageRequest,
  LocalStorageChatRequest,
} from '@/types';
import { isChatMigrateApiAvailable } from '@/utils/backendSurfaces';
import { LocalStorageChatMessageRequestRole } from '@heidi/schema/ml-scribe';
import { getDefaultStore } from 'jotai';

import { invalidateChats } from '@/hooks/query/useQueryChats';

/** Single localStorage key for all offline chat data. Value: Record<chatId, ChatResponse>. */
export const CHAT_CACHE_STORAGE_KEY = 'scribe:chats';

function messageToMigrationMessage(
  m: ChatMessageResponse
): LocalStorageChatMessageRequest {
  const role =
    m.role?.toLowerCase() === 'user'
      ? LocalStorageChatMessageRequestRole.user
      : LocalStorageChatMessageRequestRole.assistant;
  const content = typeof m.content === 'string' ? m.content : '';
  return {
    role,
    content,
    ...(m.message_id != null && { message_id: m.message_id }),
    ...(m.timestamp != null && { timestamp: m.timestamp }),
    ...(m.mode != null && { mode: m.mode }),
    ...(m.original_mode != null && { original_mode: m.original_mode }),
    ...(m.metadata != null && { metadata: m.metadata }),
    ...(m.attachments != null &&
      m.attachments.length > 0 && { attachments: m.attachments }),
  };
}

function chatToRequest(data: ChatResponse): LocalStorageChatRequest | null {
  if (!data?.title || !Array.isArray(data.messages)) return null;
  const messages = data.messages
    .filter(
      (m) =>
        m?.role &&
        (m.role.toLowerCase() === 'user' || m.role.toLowerCase() === 'assistant')
    )
    .map(messageToMigrationMessage);
  if (messages.length === 0) return null;
  return {
    title: data.title,
    messages,
    is_saved: data.is_saved ?? false,
    completion_in_progress: data.completion_in_progress ?? false,
    ...(data.id != null && { id: data.id }),
    ...(data.updated_at != null && { updated_at: data.updated_at }),
    ...(data.mode != null && { mode: data.mode }),
    ...(data.created_at != null && { created_at: data.created_at }),
  };
}

/**
 * Reads the single chat cache from localStorage, maps each stored ChatResponse to
 * LocalStorageChatRequest (title, messages with full metadata, id, updated_at, mode, etc.)
 * so migrated chats render the same. Returns [] if not in browser or no chats.
 */
function getChatsFromLocalStorage(): LocalStorageChatRequest[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(CHAT_CACHE_STORAGE_KEY);
    if (!raw) return [];
    const record = JSON.parse(raw) as Record<string, ChatResponse>;
    if (!record || typeof record !== 'object') return [];
    const chats: LocalStorageChatRequest[] = [];
    for (const data of Object.values(record)) {
      const req = chatToRequest(data);
      if (req) chats.push(req);
    }
    return chats;
  } catch {
    return [];
  }
}

/** Removes the single chat cache key from localStorage. */
function clearChatsFromLocalStorage(): void {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(CHAT_CACHE_STORAGE_KEY);
}

/**
 * Merges one chat into the single localStorage cache (read-modify-write).
 * No-op when not in browser or on parse/serialize errors.
 */
export function updateChatInLocalStorage(chatId: string, data: ChatResponse): void {
  if (typeof window === 'undefined') return;
  try {
    const raw = window.localStorage.getItem(CHAT_CACHE_STORAGE_KEY);
    const cache = raw ? (JSON.parse(raw) as Record<string, ChatResponse>) : {};
    cache[chatId] = data;
    window.localStorage.setItem(CHAT_CACHE_STORAGE_KEY, JSON.stringify(cache));
  } catch {
    // Ignore quota or serialization errors
  }
}

/**
 * If there are chats in localStorage (saved while unauthenticated), calls
 * POST /chat/migrate to store them under the current user, then clears those
 * chats from localStorage and invalidates the chat list. Call this only when
 * backend has not already migrated (chats_migrated_count === 0). On failure,
 * leaves chats in localStorage for a later retry.
 * Guarded via migrationInFlightAtom so multiple callers cannot run migrations concurrently.
 */
export async function migrateChatsFromLocalStorage(): Promise<void> {
  // `POST /chat/migrate` stays in ml-scribe's cloud-only chat router, so an
  // on-prem build has nowhere to send cached chats (a profile carried over from
  // cloud, a shared machine). They hold clinician-typed text, so drop them
  // rather than keep them in the browser.
  if (!isChatMigrateApiAvailable) {
    clearChatsFromLocalStorage();
    return;
  }
  const store = getDefaultStore();
  if (store.get(migrationInFlightAtom)) return;
  const chats = getChatsFromLocalStorage();
  if (chats.length === 0) return;
  store.set(migrationInFlightAtom, true);
  try {
    await storeChatsFromLocalStorage({ chats });
    clearChatsFromLocalStorage();
    void invalidateChats();
  } catch {
    // Leave chats in localStorage so user can retry after next auth
  } finally {
    store.set(migrationInFlightAtom, false);
  }
}
